import { useMemo, useState } from "react";

import {
  Avatar,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  AddCarrierToNetworkButton,
  FollowShipperRequestSource,
} from "components/Shipper/components/AddCarrierToNetworkButton";
import { useCarrierMemberStats } from "hooks";
import { getFullName } from "utils";

import { CarrierTeamRowMenu } from "./CarrierTeamRowMenu";
import {
  formatBookingsTooltip,
  formatMemberSince,
  formatQuotesTooltip,
  formatRatedLoadsTooltip,
  initialsOf,
  nextSortState,
  sortMembers,
} from "./utils";

import type { CarrierMemberStats } from "types/company/carrier";

import type { SortKey, SortOrder } from "./utils";

interface Props {
  companyId: number;
  companyName: string;
}

interface SatisfactionCellProps {
  member: CarrierMemberStats;
}

function SatisfactionCell(props: SatisfactionCellProps) {
  const { member } = props;
  const { rating, micro_reviews_count: count } = member.micro_review_rating;

  if (rating == null) {
    return (
      <Tooltip title="Nobody has rated this person on Evotrux yet.">
        <Typography variant="body1" component="span" color="text.secondary">
          -
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={formatRatedLoadsTooltip(count)}>
      <Stack
        direction="row"
        spacing={0.5}
        justifyContent="flex-end"
        alignItems="baseline"
      >
        <Typography variant="body1">{rating}%</Typography>
        <Typography variant="body2" color="text.secondary">
          ({count})
        </Typography>
      </Stack>
    </Tooltip>
  );
}

/**
 * The people at this carrier company and what each has done on Evotrux.
 * Shippers also see partner state and can block a person; blocking is person
 * to person, so it never affects the rest of the company.
 */
export function CarrierTeam(props: Props) {
  const { companyId, companyName } = props;
  const { members, loading, error, isShipper } = useCarrierMemberStats({
    companyId,
  });

  const [sortKey, setSortKey] = useState<SortKey>("bookings");
  const [order, setOrder] = useState<SortOrder>("desc");
  // Blocks made on this visit, shown straight away rather than after a refetch
  // of the whole list. Earlier blocks arrive as `is_blocked`.
  const [blockedRoleIds, setBlockedRoleIds] = useState<number[]>([]);

  const rows = useMemo(
    () => sortMembers({ members: members ?? [], sortKey, order }),
    [members, sortKey, order],
  );

  // Anyone the member list is closed to (a carrier on another company's
  // profile) gets no section at all.
  if (!loading && error == null && (members == null || members.length === 0)) {
    return null;
  }

  const onSort = (clickedKey: SortKey) => {
    const next = nextSortState({ current: { sortKey, order }, clickedKey });

    setSortKey(next.sortKey);
    setOrder(next.order);
  };

  const headCell = (
    key: SortKey,
    label: string,
    align: "left" | "right" = "left",
  ) => (
    <TableCell align={align}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? order : "asc"}
        onClick={() => onSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Stack sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Team
      </Typography>
      <EvotruxDivider />
      <ErrorTypography error={error} />
      <LoadingWrappedNode loading={loading}>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead
              sx={{
                "& th": {
                  backgroundColor: "grey.100",
                  fontWeight: 600,
                  color: "text.secondary",
                },
              }}
            >
              <TableRow>
                {headCell("name", "Name")}
                {headCell("memberSince", "Member since")}
                {headCell("quotes", "Quotes", "right")}
                {headCell("bookings", "Bookings", "right")}
                {headCell("satisfaction", "Satisfaction", "right")}
                {isShipper && <TableCell align="right">Partner</TableCell>}
                {isShipper && <TableCell sx={{ width: 56 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((member) => {
                const name = getFullName(member.first_name, member.last_name);
                const isBlocked =
                  (member.is_blocked ?? false) ||
                  blockedRoleIds.includes(member.role_id);
                const memberSince = formatMemberSince(member.member_since);

                return (
                  <TableRow key={member.role_id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                          {initialsOf(member)}
                        </Avatar>
                        <Typography variant="body1">{name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={`${name} joined Evotrux in ${memberSince}.`}
                      >
                        <Typography variant="body1" component="span">
                          {memberSince}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={formatQuotesTooltip(member.quotes_created)}
                      >
                        <Typography variant="body1" component="span">
                          {member.quotes_created.toLocaleString()}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={formatBookingsTooltip(member.loads_booked)}
                      >
                        <Typography variant="body1" component="span">
                          {member.loads_booked.toLocaleString()}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <SatisfactionCell member={member} />
                    </TableCell>
                    {isShipper && (
                      <TableCell align="right">
                        {/* Blocking also removes the person from this
                        shipper's network, so Blocked replaces partner state. */}
                        {isBlocked && (
                          <Chip
                            label="Blocked"
                            size="small"
                            variant="outlined"
                            color="error"
                          />
                        )}
                        {!isBlocked && member.is_partner && (
                          <Tooltip title="This person is a partner in your carrier network.">
                            <Chip
                              label="Partner"
                              size="small"
                              variant="outlined"
                              sx={{ color: "text.secondary" }}
                            />
                          </Tooltip>
                        )}
                        {!isBlocked && !member.is_partner && (
                          <AddCarrierToNetworkButton
                            carrierId={member.role_id}
                            source={
                              FollowShipperRequestSource.CARRIER_PROFILE_TEAM
                            }
                            display="link"
                          />
                        )}
                      </TableCell>
                    )}
                    {isShipper && (
                      <TableCell align="right" sx={{ py: 0 }}>
                        {/* No unblock in the app: once blocked, the menu goes. */}
                        {!isBlocked && (
                          <CarrierTeamRowMenu
                            member={member}
                            companyName={companyName}
                            onBlocked={() =>
                              setBlockedRoleIds((ids) => [
                                ...ids,
                                member.role_id,
                              ])
                            }
                          />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </LoadingWrappedNode>
    </Stack>
  );
}
